import { createContext, FC, ReactNode, useCallback, useContext, useMemo, useRef, useState } from "react"
import { useModalManager } from "./modalManager"
import { useTabs, parseTabId } from "@/contexts/tabsContext"
import { useNotificationToastApi } from "@/hooks/useNotificationToastApi"
import { useNavigatorApi, useNavigatorDriver } from "./navigator"
import { useService } from "@/hooks/useService"
import { AGENT_SERVICE_V2_IDENT, type IAgentServiceV2, type Agent, type InviteV2AgentResult, type InviteV2AgentErrorCodes } from "@bloomstack/apis/agentTypes"
import { RPC_TIER_GLOBAL } from "@/constants/rpcPriorities"
import type { Result } from "@bloomstack/greenhouse/results"
import { Spinner } from "@/components/ui/spinner"
import Dynamic from '@/lib/dynamic'
import ComponentSpinner from "@/components/ui/componentSpinner";

import { Dialog2 } from "@/components/ui/dialog2";

const AgentBrowser = Dynamic(() => import("@/components/agents/AgentBrowser").then(mod => ({ default: mod.AgentBrowser })), { ssr: false, loading: () => <ComponentSpinner /> });

export type AgentLibraryContextType = {
  openAgentLibrary: (id?: string, mode?: "view" | "edit", hideInvite?: boolean) => void;
  inviteAgent: (agentId: string, roomId: string) => Promise<Result<InviteV2AgentResult, InviteV2AgentErrorCodes>>;
  cloneAgent: (agentId: string) => Promise<Agent | null>;
}

export const AgentLibraryContext = createContext<AgentLibraryContextType | undefined>(undefined)

export const useAgentLibrary = () => {
  const context = useContext(AgentLibraryContext)
  if (!context) {
    throw new Error("useAgentLibrary must be used within an AgentLibraryProvider")
  }
  return context
}

export const AgentLibraryProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const modal = useModalManager();
  const nav = useNavigatorApi();
  const tabs = useTabs(false);
  const notifications = useNotificationToastApi();
  const agentServiceV2 = useService<IAgentServiceV2>(AGENT_SERVICE_V2_IDENT)
    .withPriority('normal', () => RPC_TIER_GLOBAL);
  const modalCloseRef = useRef<(() => void) | null>(null);
  
  const activeTabId = tabs?.getActiveTabId();
  const activeTabParsed = activeTabId ? parseTabId(activeTabId) : null;
  const roomIdFromTabs = activeTabParsed?.type === 'workspace' ? activeTabParsed.entityId : undefined;
  const roomId = roomIdFromTabs;

  // Invite an agent to a workspace room
  // Uses agentServiceV2.invite() which handles clone-if-needed + inviteToStream internally
  const inviteAgent = useCallback(async (agentId: string, targetRoomId: string): Promise<Result<InviteV2AgentResult, InviteV2AgentErrorCodes>> => {
    const response = await agentServiceV2.invite(agentId, targetRoomId);
    if (response.success) {
      notifications.showNotification({
        title: "Agent invited",
        message: response.result.cloned
          ? "Agent has been cloned and invited to the room"
          : "Agent has been invited to the room"
      });
    } else {
      notifications.showNotification({
        title: response.error?.code || "Error",
        message: response.error?.reason || "Failed to invite agent"
      });
    }
    return response;
  }, [agentServiceV2, notifications]);

  // Clone an agent (explicit user action)
  const cloneAgent = useCallback(async (agentId: string): Promise<Agent | null> => {
    const response = await agentServiceV2.clone(agentId);
    if (response.success) {
      return response.result;
    } else {
      notifications.showNotification({
        title: response.error?.code || "Error",
        message: response.error?.reason || "Failed to clone agent"
      });
      return null;
    }
  }, [agentServiceV2, notifications]);

  // Wrapper for AgentBrowser — uses active room from tabs
  const handleAgentInvite = useCallback(async (agentId: string) => {
    if (roomId) {
      const result = await inviteAgent(agentId, roomId);
      if (result.success) {
        modalCloseRef.current?.();
      }
    }
  }, [inviteAgent, roomId]);

  const openAgentLibrary = useCallback((id?: string, mode?: "view" | "edit", hideInvite?: boolean) => {
    modal.show((api) => {
      modalCloseRef.current = () => api.close();
      return <Dialog2
        onClose={() => api.close()}
        className="w-[100dvw] h-[100dvh] rounded-none border-0 shadow-none md:h-[90dvh] md:w-[90dvw] md:max-w-6xl md:rounded-md md:border md:shadow-md"
        contentClassName="!gap-0"
        actionsClassName="absolute top-3 right-3 z-20 md:relative md:top-auto md:right-auto md:z-auto md:p-2"
      >
        <AgentBrowser
          onClose={() => api.close()}
          onInvite={roomId && !hideInvite ? handleAgentInvite : undefined}
          initialAgentId={id}
          initialMode={mode}
          hasActiveWorkspace={!!roomId}
        />
      </Dialog2>;
    });    
  }, [modal, handleAgentInvite, roomId]);

  const openAgentLibraryFromUserId = useCallback(async (userId: string) => {
    // we'll need to first fetch the agent by user ID
    // before we can open the library
    const fetchAgent = await agentServiceV2.withTimeout(10000).readByUserId(userId);
    if (!fetchAgent.success) {
      notifications.showNotification({
        title: "Error",
        message: fetchAgent.error.reason || "Failed to load agent by user ID"
      });
      return;
    }

    openAgentLibrary(fetchAgent.result.id);
  }, [agentServiceV2, notifications, openAgentLibrary]);

  // Helper to show loading modal while fetching agent
  const withLoadingModal = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | null> => {
    const mInstance = modal.show(() => <Dialog2
      title="Loading Agent"
      className="h-[50dvh] w-[50dvw]"
    >
      <div className="flex flex-col items-center justify-center h-full">
        <Spinner size="64px" />
      </div>
    </Dialog2>);
    
    try {
      return await fn();
    } finally {
      mInstance.close();
    }
  }, [modal]);

  useNavigatorDriver(async (route) => {
    switch(route.route) {
      case "agent-list":
        // Navigate back to the agent list (clear any active route)
        nav.clearRoute();
        break;
      case "agent-clone":
        nav.clearRoute();
        if (route.props.agentId) {
          const cloned = await cloneAgent(route.props.agentId);
          if (cloned) {
            openAgentLibrary(cloned.id);
          }
        }
        break;
      case "agent-create":
      case "agent-library":
        nav.clearRoute();
        openAgentLibrary();
        break;
      case "agent-edit":
        nav.clearRoute();
        let agentId = route.props.agentId;
        
        // Priority: agentId > agentUserProfileId (V2) > agentUserId (legacy)
        if (agentId) {
          // Direct agent ID - use it
          openAgentLibrary(agentId, "edit", route.props.hideInvite);
        } else if (route.props.agentUserProfileId) {
          // User profile ID - use V2 service (preferred)
          const result = await withLoadingModal(async () => {
            return agentServiceV2.readByUserProfileId(route.props.agentUserProfileId!);
          });
          
          if (!result?.success) {
            notifications.showNotification({
              title: "Error",
              message: result?.error?.reason || "Failed to load agent by user profile ID"
            });
            return;
          }
          openAgentLibrary(result.result.id, "edit", route.props.hideInvite);
        } else if (route.props.agentUserId) {
          // System user ID - use typed service
          const result = await withLoadingModal(async () => {
            return agentServiceV2.withTimeout(10000).readByUserId(route.props.agentUserId!);
          });
          
          if (!result?.success) {
            notifications.showNotification({
              title: "Error",
              message: "Failed to load agent by user ID"
            });
            return;
          }
          openAgentLibrary(result?.result?.id, "edit");
        }
        break;
      case "agent-view":
        nav.clearRoute();
        // Priority: agentUserProfileId (V2) > agentUserId (legacy) > agentId
        if (route.props.agentUserProfileId) {
          const result = await withLoadingModal(async () => {
            return agentServiceV2.readByUserProfileId(route.props.agentUserProfileId!);
          });
          
          if (!result?.success) {
            notifications.showNotification({
              title: "Error",
              message: result?.error?.reason || "Failed to load agent by user profile ID"
            });
            return;
          }
          openAgentLibrary(result.result.id, "view", route.props.hideInvite);
        } else if (route.props.agentUserId) {
          openAgentLibraryFromUserId(route.props.agentUserId);
        } else {
          openAgentLibrary(route.props.agentId, "view", route.props.hideInvite);
        }
        break;
    }
  }, [openAgentLibrary, openAgentLibraryFromUserId, withLoadingModal, agentServiceV2, notifications, cloneAgent]);

  const api = useMemo(() => ({
    openAgentLibrary,
    inviteAgent,
    cloneAgent,
  }), [openAgentLibrary, inviteAgent, cloneAgent]);

  return (
    <AgentLibraryContext.Provider value={api}>
      {children}
    </AgentLibraryContext.Provider>
  )
}